import { registerMockAPI } from '~/mocks/mockApis';

import { useMutation } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { RECEIVE } from '~/constants/errorCode';

import type {
  TypeApiResponse,
  TypeFailItem,
  TypePostRequestGoodsUploadParams,
  TypePostRequestGoodsUploadResponse,
  TypeSuccessItem,
} from '~/types/receive.types';

type UploadParams = {
  ownerCode: string;
  params: TypePostRequestGoodsUploadParams;
};

/**
 * [입고요청등록] 입고 상품 엑셀 업로드 API
 */
const fetchPostRequestGoodsUpload = async ({ ownerCode, params }: UploadParams) => {
  const endpoint = '/api-inbound/v1/3pl/receiving';
  const { data } = await axiosInstance.post<TypeApiResponse<TypePostRequestGoodsUploadResponse>>(
    `${endpoint}/${ownerCode}/request/goods/upload`,
    params,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return data.data;
};

/**
 * [입고요청등록] 입고 상품 엑셀 업로드 Mutation Hook
 */
export const usePostRequestGoodsUploadMutation = () => {
  return useMutation({
    mutationFn: fetchPostRequestGoodsUpload,
    meta: { showGlobalErrorToast: false }, // 전역 에러 핸들러 비활성화 (사용처에서 개별 처리)
  });
};

const successItemPresets: Array<
  Pick<TypeSuccessItem, 'manufactureDate' | 'expirationDate' | 'boxQty' | 'boxPerQty' | 'eachQty'>
> = [
  { manufactureDate: '2026-05-01', expirationDate: '2027-05-01', boxQty: 50, boxPerQty: 24, eachQty: 0 },
  { manufactureDate: '2026-04-15', expirationDate: '2026-10-15', boxQty: 20, boxPerQty: 48, eachQty: 500 },
  { manufactureDate: '', expirationDate: '', boxQty: 2, boxPerQty: 100, eachQty: 0 },
];

const buildSuccessItem = (idx: number): TypeSuccessItem => {
  const preset = successItemPresets[(idx - 1) % successItemPresets.length];
  return {
    goodsId: 17000 + idx,
    goodsCode: `EN000001${String(idx).padStart(4, '0')}`,
    nsid: `NSID-${String(idx).padStart(7, '0')}`,
    alternativeCode: `880000${String(idx).padStart(7, '0')}`,
    goodsName: `테스트 상품 ${idx}`,
    kurlyKeepTypeDesc: '상온',
    palletYn: false,
    lotNo: '',
    ...preset,
  };
};

type FailItemOverrides = Partial<
  Pick<TypeFailItem, 'manufactureDate' | 'expirationDate' | 'boxQty' | 'boxPerQty' | 'eachQty'>
>;

const buildFailItem = (idx: number, failReason: string, overrides: FailItemOverrides = {}): TypeFailItem => ({
  goodsCode: `INVALID-${idx}`,
  nsid: '',
  manufactureDate: '',
  expirationDate: '',
  palletYn: 'N',
  boxQty: 0,
  boxPerQty: 0,
  eachQty: 0,
  failReason,
  ...overrides,
});

const allSuccessResponse: TypeApiResponse<TypePostRequestGoodsUploadResponse> = {
  data: {
    totalCount: 3,
    successCount: 3,
    successItems: [buildSuccessItem(1), buildSuccessItem(2), buildSuccessItem(3)],
    failCount: 0,
    failItems: [],
  },
};

const partialFailResponse: TypeApiResponse<TypePostRequestGoodsUploadResponse> = {
  data: {
    totalCount: 5,
    successCount: 3,
    successItems: [buildSuccessItem(1), buildSuccessItem(2), buildSuccessItem(3)],
    failCount: 2,
    failItems: [
      buildFailItem(101, '존재하지 않는 상품 코드입니다.', {
        manufactureDate: '2026-06-01',
        expirationDate: '2027-06-01',
        boxQty: 1200,
        boxPerQty: 24,
        eachQty: 0,
      }),
      buildFailItem(102, '수량을 확인해 주세요.', {
        boxQty: 15000,
        boxPerQty: 1,
        eachQty: 500,
      }),
    ],
  },
};

const partialFailWithRawQtyResponse: TypeApiResponse<TypePostRequestGoodsUploadResponse> = {
  data: {
    totalCount: 3,
    successCount: 2,
    successItems: [buildSuccessItem(1), buildSuccessItem(2)],
    failCount: 1,
    failItems: [
      buildFailItem(103, '수량은 숫자만 입력해 주세요.', {
        boxQty: '10박스',
        boxPerQty: '24',
        eachQty: '',
      }),
    ],
  },
};

registerMockAPI<TypeApiResponse<TypePostRequestGoodsUploadResponse>>({
  method: 'post',
  endpoint: '/api-inbound/v1/3pl/receiving/:ownerCode/request/goods/upload',
  label: '입고/입고요청/엑셀업로드',
  scenarios: [
    { name: '정상 응답 — 전건 성공', response: allSuccessResponse },
    { name: '정상 응답 — 부분 실패', response: partialFailResponse },
    { name: '정상 응답 — 부분 실패 (수량 문자열)', response: partialFailWithRawQtyResponse },
    {
      name: '행수 초과 (9104)',
      response: {
        error: {
          code: RECEIVE.ROW_COUNT_EXCEEDED,
          message: '최대 200개의 행까지만 등록 가능합니다. 확인 후 다시 시도해주세요.',
        },
      },
      status: 400,
    },
    {
      name: 'SKU/수량 초과 (9020)',
      response: {
        error: {
          code: RECEIVE.SKU_AND_QUANTITY_COUNT_EXCEEDED,
          message: '최대 200종의 품목, 20,000개의 수량까지만 등록 가능합니다. 확인 후 다시 시도해주세요.',
        },
      },
      status: 400,
    },
  ],
});
